import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { notify } from "../components/Toast";
import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api } from "../api/client";
import CalendarGrid from "../components/CalendarGrid";
import CalendarSearch from "../components/CalendarSearch";
import WeekGrid from "../components/WeekGrid";
import BlockEditor from "../components/BlockEditor";
import MyDaySummary from "../components/MyDaySummary";
import BlockResources from "../components/BlockResources";
import MyWeekSummary from "../components/MyWeekSummary";
import ProgressPromptModal from "../components/ProgressPromptModal";
import RecurringPatternEditor from "../components/RecurringPatternEditor";
import SchedulableItemsPanel from "../components/SchedulableItemsPanel";
import TimeSelect from "../components/TimeSelect";
import { useAuth } from "../hooks/useAuth";
function ymd(d) {
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
// "07:00:00" -> "7:00 AM"
function fmtAwake(hms) {
    const [h, m] = hms.split(":").map(Number);
    const d = new Date();
    d.setHours(h || 0, m || 0, 0, 0);
    return d.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
    });
}
// Monday of the ISO week containing `d` (local time).
function isoMonday(d) {
    const day = d.getDay();
    const monIndex = (day + 6) % 7;
    const m = new Date(d);
    m.setDate(d.getDate() - monIndex);
    m.setHours(0, 0, 0, 0);
    return m;
}
// Mutate `target` so its time-of-day matches "now" (local clock), rounded
// down to the nearest 15-min mark. Date portion is preserved — used when
// the user opens a new block on the currently-displayed calendar day so
// the start time defaults to "right now" instead of a hard-coded 9 AM.
function setStartToNow(target) {
    const now = new Date();
    const minute = Math.floor(now.getMinutes() / 15) * 15;
    target.setHours(now.getHours(), minute, 0, 0);
    return target;
}
export default function CalendarPage() {
    const { user, refresh: refreshUser } = useAuth();
    const [view, setView] = useState("day");
    const [date, setDate] = useState(() => new Date());
    const [blocks, setBlocks] = useState([]);
    const [summary, setSummary] = useState(null);
    const [awake, setAwake] = useState(null);
    const [editorOpen, setEditorOpen] = useState(false);
    const [editorStart, setEditorStart] = useState(null);
    const [editorEnd, setEditorEnd] = useState(null);
    const [editing, setEditing] = useState(null);
    const [prefill, setPrefill] = useState(null);
    const [recurringOpen, setRecurringOpen] = useState(false);
    const [recurringPrefill, setRecurringPrefill] = useState(null);
    const [panelOpen, setPanelOpen] = useState(false);
    const [refreshTick, setRefreshTick] = useState(0);
    // The side-by-side SchedulePanel was replaced by CalendarSearch's
    // inline click-to-expand experience — picking a task or project from
    // the search now expands the row in place with a schedule form
    // underneath, rather than popping a separate panel on the right.
    // Auto-open the scheduler panel when the user lands here via
    // `navigate("/calendar", { state: { openScheduler: true } })` — used
    // by the Today page's "Schedule" buttons so the user lands ready to
    // pick a slot for the row they just clicked. Clear the state after
    // honoring it so a browser back/forward doesn't keep re-opening.
    const location = useLocation();
    const navigate = useNavigate();
    useEffect(() => {
        const wantsScheduler = location.state?.openScheduler;
        if (wantsScheduler) {
            setPanelOpen(true);
            navigate(location.pathname, { replace: true, state: null });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // Inline awake-window editor state. Click the banner to open it; the
    // start/end time pickers seed from the current effective hours so the
    // user can nudge the window without retyping. Saving writes the new
    // default to /users/me AND (when there's a per-week override active)
    // clears that override so the new times show up immediately — which
    // fixes the "Settings default change didn't update my calendar" bug
    // that left users staring at a stale override they didn't know they had.
    const [editingAwake, setEditingAwake] = useState(false);
    const [awakeStartEdit, setAwakeStartEdit] = useState("07:00");
    const [awakeEndEdit, setAwakeEndEdit] = useState("22:00");
    const [awakeSaving, setAwakeSaving] = useState(false);
    function openAwakeEditor() {
        setAwakeStartEdit((awake?.awake_start ?? user?.awake_start ?? "07:00:00").slice(0, 5));
        setAwakeEndEdit((awake?.awake_end ?? user?.awake_end ?? "22:00:00").slice(0, 5));
        setEditingAwake(true);
    }
    async function saveAwake() {
        if (!user)
            return;
        setAwakeSaving(true);
        try {
            await api.patch("/users/me", {
                awake_start: `${awakeStartEdit}:00`,
                awake_end: `${awakeEndEdit}:00`,
            });
            // If the displayed date had a per-week override masking the
            // default, clear it so the new default takes effect on this
            // week. The endpoint 404s gracefully when nothing exists for
            // the week, so we swallow that case.
            if (awake?.is_overridden) {
                const monday = isoMonday(date);
                try {
                    await api.del(`/users/me/awake-schedules/${ymd(monday)}`);
                }
                catch {
                    /* ignore — override may have been cleared by another tab */
                }
            }
            await refreshUser();
            await reload();
            setEditingAwake(false);
        }
        catch (e) {
            notify.error(e instanceof Error ? e.message : "Couldn't save awake hours");
        }
        finally {
            setAwakeSaving(false);
        }
    }
    // Ticks every 5 minutes — passed to the grids so the "Now" line drifts
    // down through the day without a hard refresh. Also re-evaluates "is this
    // block in the past?" for the progress button affordance.
    const [nowTick, setNowTick] = useState(0);
    useEffect(() => {
        const id = window.setInterval(() => setNowTick((t) => t + 1), 5 * 60 * 1000);
        return () => window.clearInterval(id);
    }, []);
    // Finer "now" (60s) for the active-block widget so its "X min left"
    // countdown stays roughly accurate without a hard refresh.
    const [nowMs, setNowMs] = useState(() => Date.now());
    useEffect(() => {
        const id = window.setInterval(() => setNowMs(Date.now()), 60 * 1000);
        return () => window.clearInterval(id);
    }, []);
    // Progress-prompt modal state. When a user clicks "Input progress %" on a
    // past block, we open this modal targeting the block's linked task.
    const [progressBlock, setProgressBlock] = useState(null);
    const reload = useCallback(async () => {
        if (!user)
            return;
        // Send the browser's IANA timezone with the range query so the backend
        // uses where the user actually is (rather than a stale profile tz) when
        // converting from_d/to_d into UTC bounds. Fixes the case where a block
        // appears in the week view but not the day view because the profile
        // tz pushed the local-day window off the block's UTC start.
        const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
        const tzQ = `&tz=${encodeURIComponent(browserTz)}`;
        if (view === "day") {
            const dayStr = ymd(date);
            const next = new Date(date);
            next.setDate(next.getDate() + 1);
            const [bs, sum, eff] = await Promise.all([
                api.get(`/time-blocks?from=${dayStr}&to=${ymd(next)}${tzQ}`),
                api.get(`/time-blocks/me/day-summary/${dayStr}`),
                api.get(`/users/me/awake-hours/${dayStr}`),
            ]);
            setBlocks(bs);
            setSummary(sum);
            setAwake(eff);
        }
        else {
            const monday = isoMonday(date);
            const sunday = new Date(monday);
            sunday.setDate(monday.getDate() + 7);
            const [bs, eff] = await Promise.all([
                api.get(`/time-blocks?from=${ymd(monday)}&to=${ymd(sunday)}${tzQ}`),
                // Week awake hours: same for the whole ISO week, so resolve at the Monday.
                api.get(`/users/me/awake-hours/${ymd(monday)}`),
            ]);
            setBlocks(bs);
            setAwake(eff);
            setSummary(null); // summary is per-day; not shown in week view
        }
        setRefreshTick((t) => t + 1);
    }, [date, user, view]);
    useEffect(() => {
        reload();
    }, [reload]);
    // Re-fetch when the tab regains focus. Fixes the "I changed my default
    // awake hours in Settings and the calendar still shows the old window"
    // bug — Settings saves don't trigger a Calendar re-fetch on their own,
    // so we treat focus as the user signaling "I'm back, refresh." Same
    // pattern covers any other tab-side mutation (Projects & Meetings,
    // Admin team view) that affects what the calendar should display.
    useEffect(() => {
        function onFocus() {
            if (!document.hidden)
                reload();
        }
        window.addEventListener("focus", onFocus);
        document.addEventListener("visibilitychange", onFocus);
        return () => {
            window.removeEventListener("focus", onFocus);
            document.removeEventListener("visibilitychange", onFocus);
        };
    }, [reload]);
    function shift(delta) {
        const d = new Date(date);
        if (view === "day")
            d.setDate(d.getDate() + delta);
        else
            d.setDate(d.getDate() + delta * 7);
        setDate(d);
    }
    function onSlotCreate(start, end) {
        setEditing(null);
        setPrefill(null);
        setEditorStart(start);
        setEditorEnd(end);
        setEditorOpen(true);
    }
    function onBlockSelect(b) {
        setEditing(b);
        setPrefill(null);
        setEditorOpen(true);
    }
    // Past-block progress prompt opener — fired by CalendarGrid's onProgressPrompt.
    function openProgressPrompt(b) {
        setProgressBlock(b);
    }
    // After progress is saved at <100%, offer to schedule a follow-up block to
    // continue the work. We default the follow-up to "tomorrow, at the same
    // time of day as right now" so the start matches the user's working
    // rhythm instead of a hard 9 AM. They can adjust before saving.
    function scheduleFollowUp(forBlock) {
        // Anchor the follow-up to TODAY at the current (15-min-rounded)
        // time — NOT tomorrow. The seed time is what gets sent to the
        // open-slots endpoint as the search center, so anchoring to today
        // makes the "nearest open slots" chips surface today's free
        // windows first instead of jumping a day ahead. (Previously this
        // did `setDate(+1)`, which is why a reschedule kept landing on
        // tomorrow.) The backend now floors suggestions at "now" so the
        // backward half of its ±72h search can't surface already-passed
        // slots earlier today.
        const followUp = setStartToNow(new Date());
        const end = new Date(followUp.getTime() + 30 * 60_000);
        setEditing(null);
        setEditorStart(followUp);
        setEditorEnd(end);
        setPrefill({
            // Reuse the original title (already in "Project — Task" form) so the
            // follow-up keeps the breadcrumb. Also re-link the checklist item so
            // remaining-minutes accounting picks it up. For plain-task blocks
            // (no checklist), forward the task id too so the BlockEditor can
            // auto-select the matching kill-list row (the "Not yet" path just
            // re-added it) instead of opening with an empty dropdown.
            title: forBlock.title,
            checklist_item_id: forBlock.checklist_item_id,
            taskId: forBlock.task_id ?? undefined,
            duration_minutes: 30,
            block_type: "lockout",
            titleLocked: true,
            // Offer the nearest open slots up front — rescheduling should hand the
            // user the closest free times to drop the follow-up into, not make them
            // hunt for a gap.
            offerSlots: true,
        });
        setEditorOpen(true);
    }
    // Drag-and-drop reschedule. Preserves duration, sends a PATCH with the new
    // start/end, and reloads. The server already enforces overlap + flex-ratio
    // rules — surface its 409/400 message verbatim so the user knows why it
    // bounced. On success the calendar refetches via `reload()`.
    async function onBlockMove(b, newStart) {
        const oldStart = new Date(b.start_at);
        const oldEnd = new Date(b.end_at);
        const duration = oldEnd.getTime() - oldStart.getTime();
        // No-op if the drop landed on the same minute (browsers fire drop even on
        // a noop "drag in place"; avoid the round-trip).
        if (newStart.getTime() === oldStart.getTime())
            return;
        const newEnd = new Date(newStart.getTime() + duration);
        try {
            await api.patch(`/time-blocks/${b.id}`, {
                start_at: newStart.toISOString(),
                end_at: newEnd.toISOString(),
            });
            reload();
        }
        catch (e) {
            notify.error(e instanceof Error ? e.message : "Couldn't move that block.");
        }
    }
    function scheduleAsBlock(item) {
        // Default to a 30-min Deep Focus slot. There are no time estimates
        // anymore (revolving schedule), so we don't auto-size or auto-chain
        // multiple blocks — the user picks each session deliberately, and the
        // editor nudges them to break long work into separate sittings.
        const useDuration = 30;
        const start = new Date(date);
        setStartToNow(start);
        const end = new Date(start);
        end.setMinutes(start.getMinutes() + useDuration);
        setEditing(null);
        setEditorStart(start);
        setEditorEnd(end);
        setPrefill({
            // "Project — Item" so the calendar block always carries the project
            // context even when the user only scans block titles. The full task
            // title is preserved as the right-hand side.
            title: `${item.project_name} — ${item.title}`,
            checklist_item_id: item.id,
            duration_minutes: useDuration,
            // Always default to Deep Focus when scheduling from a task; the user
            // can switch to Flex/Personal manually if they want a softer slot.
            block_type: "lockout",
            titleLocked: true,
        });
        setEditorOpen(true);
    }
    function scheduleAsRecurring(task) {
        setRecurringPrefill(task);
        setRecurringOpen(true);
    }
    // Schedule a project (no task, no checklist item) — useful when a project
    // has no tasks yet or the user wants a generic "work on this project" block.
    // Title is the project name; block isn't linked to anything so no progress
    // prompt fires after the end_at passes.
    function scheduleProjectAsBlock(project) {
        const start = new Date(date);
        setStartToNow(start);
        const end = new Date(start);
        end.setMinutes(start.getMinutes() + 30);
        setEditing(null);
        setEditorStart(start);
        setEditorEnd(end);
        setPrefill({
            title: project.name,
            duration_minutes: 30,
            block_type: "lockout",
            titleLocked: true,
        });
        setEditorOpen(true);
    }
    // Schedule a personal to-do as a Personal block — title carried over, block
    // type forced to "personal" so the existing private-block rules apply (the
    // title is hidden as "Personal" to everyone but the owner). Not linked back
    // to the to-do row; the item stays on the list until the user checks it off.
    function schedulePersonalTodo(todo) {
        const start = new Date(date);
        setStartToNow(start);
        const end = new Date(start);
        end.setMinutes(start.getMinutes() + 30);
        setEditing(null);
        setEditorStart(start);
        setEditorEnd(end);
        setPrefill({
            title: todo.title,
            description: todo.description ?? undefined,
            duration_minutes: 30,
            block_type: "personal",
            // Personal blocks are owner-renamable; don't lock the title.
            titleLocked: false,
        });
        setEditorOpen(true);
    }
    // Schedule a Personal block toward a personal goal for the displayed day.
    // Personal goals recur, so this just drops a session on the calendar
    // (titled after the goal) — no standing to-do is created; the user can do
    // this each day they want to work the goal.
    function scheduleGoalAsBlock(goal) {
        const start = new Date(date);
        setStartToNow(start);
        const end = new Date(start);
        end.setMinutes(start.getMinutes() + 30);
        setEditing(null);
        setEditorStart(start);
        setEditorEnd(end);
        setPrefill({
            title: goal.title,
            description: goal.why ?? undefined,
            duration_minutes: 30,
            block_type: "personal",
            titleLocked: false,
            // Link the session to the goal so completing it logs units, not time.
            // No per-session target on the single-session path — the user enters
            // the amount at completion; goal_unit still drives the prompt's label.
            goalId: goal.id,
            goalSessionTarget: null,
            goalUnit: goal.unit ?? null,
        });
        setEditorOpen(true);
    }
    // Smart multi-day scheduling for a goal: drop a Personal block on each of
    // the chosen dates in one action (the "schedule numerous dates" ask). Each
    // block starts at the current time-of-day on its date so same-day siblings
    // don't collide across different days. `perSession` (+ unit) is folded into
    // the block title for quantity goals ("Run 750 miles — 6 mi") so the
    // calendar reminds the user how much to do that session. Returns a
    // {scheduled, failed} tally; per-day overlaps 409 and count as failed
    // rather than aborting the whole batch.
    async function scheduleGoalDays(goal, dates, opts) {
        const unit = goal.unit?.trim();
        const title = opts.perSession && unit
            ? `${goal.title} — ${opts.perSession} ${unit}`
            : opts.perSession
                ? `${goal.title} — ${opts.perSession}`
                : goal.title;
        // Parse the chosen time once; reused for every selected date.
        let hh = null;
        let mm = 0;
        if (opts.time && /^\d{1,2}:\d{2}$/.test(opts.time)) {
            const [h, m] = opts.time.split(":").map(Number);
            hh = h;
            mm = m;
        }
        let scheduled = 0;
        let failed = 0;
        for (const d of dates) {
            const start = new Date(d);
            if (hh !== null)
                start.setHours(hh, mm, 0, 0);
            else
                setStartToNow(start);
            const end = new Date(start);
            end.setMinutes(start.getMinutes() + opts.durationMinutes);
            try {
                await api.post("/time-blocks", {
                    title,
                    description: goal.why ?? null,
                    start_at: start.toISOString(),
                    end_at: end.toISOString(),
                    block_type: "personal",
                    is_personal: true,
                    checklist_item_id: null,
                    task_id: null,
                    mirror_to_user_ids: [],
                    // Link the session back to the goal so completing it logs units
                    // (not time). goal_session_target pre-fills the completion prompt
                    // with the planned per-session amount; goal_unit is the label.
                    goal_id: goal.id,
                    goal_session_target: opts.perSession ?? null,
                    goal_unit: unit || null,
                });
                scheduled += 1;
            }
            catch {
                failed += 1;
            }
        }
        await reload();
        return { scheduled, failed };
    }
    // Schedule a plain task (no checklist item) — open the block editor with the
    // task title as the prefill; the block won't be linked to a checklist item.
    function scheduleTaskAsBlock(task) {
        // Default to a 30-min Deep Focus slot. Plain tasks don't have an
        // estimated_hours, so start small; user can switch to 60 min in the editor.
        const useDuration = 30;
        const start = new Date(date);
        setStartToNow(start);
        const end = new Date(start);
        end.setMinutes(start.getMinutes() + useDuration);
        setEditing(null);
        setEditorStart(start);
        setEditorEnd(end);
        setPrefill({
            title: `${task.project_name} — ${task.title}`,
            description: task.description ?? undefined,
            duration_minutes: useDuration,
            block_type: "lockout",
            titleLocked: true,
            // Surface the task id to the BlockEditor so the POST body carries
            // `task_id` and the backend flips the task to in_progress on save.
            taskId: task.task_id,
        });
        setEditorOpen(true);
    }
    // Schedule from the Kill List — clicking a row in the Kill List tab opens
    // the BlockEditor pre-loaded with that item: title locked to the kill
    // entry's hydrated label, block_type forced to Deep Focus, default 30-min
    // duration. The user picks start date/time + duration in the editor.
    function scheduleKillItem(kill) {
        const useDuration = 30;
        const start = new Date(date);
        setStartToNow(start);
        const end = new Date(start);
        end.setMinutes(start.getMinutes() + useDuration);
        setEditing(null);
        setEditorStart(start);
        setEditorEnd(end);
        setPrefill({
            title: kill.project_name
                ? `${kill.project_name} — ${kill.title}`
                : kill.title,
            // Link the block to the kill item's checklist item when present.
            // Task-only or parking-lot kill rows leave this null — the user still
            // gets a Deep Focus block titled after the kill entry, just without
            // task-progress rollup.
            checklist_item_id: kill.checklist_item_id,
            duration_minutes: useDuration,
            block_type: "lockout",
            titleLocked: true,
            // Pass the kill row's own ID so the BlockEditor can pre-select it
            // in the "From your Kill List" dropdown when no checklist linkage
            // is available (task-only kills).
            killListItemId: kill.id,
            // Task-only kill rows: feed the task id through so the backend
            // flips the parent task to in_progress on block create (the
            // checklist-item path handles that itself via ci.task_id).
            taskId: kill.task_id ?? undefined,
        });
        setEditorOpen(true);
    }
    if (!user)
        return null;
    const awakeStart = awake?.awake_start ?? user.awake_start;
    const awakeEnd = awake?.awake_end ?? user.awake_end;
    const monday = isoMonday(date);
    return (_jsxs("div", { className: `page calendar-page ${panelOpen ? "with-panel" : ""}`, children: [_jsxs("div", { className: "calendar-main", children: [_jsxs("header", { className: "page-header", children: [_jsxs("div", { className: "day-nav", children: [_jsx("button", { onClick: () => shift(-1), children: "\u2039" }), _jsx("h1", { children: view === "day"
                                            ? date.toLocaleDateString(undefined, {
                                                weekday: "long",
                                                year: "numeric",
                                                month: "long",
                                                day: "numeric",
                                            })
                                            : `Week of ${monday.toLocaleDateString(undefined, {
                                                month: "short",
                                                day: "numeric",
                                            })}` }), _jsx("button", { onClick: () => shift(1), children: "\u203A" }), _jsx("button", { onClick: () => setDate(new Date()), children: "Today" })] }), _jsxs("div", { className: "view-toggle", children: [_jsx("button", { className: view === "day" ? "active" : "", onClick: () => setView("day"), children: "Day" }), _jsx("button", { className: view === "week" ? "active" : "", onClick: () => setView("week"), children: "Week" })] }), _jsx(CalendarSearch, { onScheduled: () => setRefreshTick((t) => t + 1) }), _jsxs("div", { className: "header-actions", children: [_jsx("button", { onClick: () => setPanelOpen((v) => !v), children: panelOpen ? "Hide Scheduler" : "Scheduler" }), _jsx("button", { className: "btn-primary", onClick: () => {
                                            const start = new Date(date);
                                            setStartToNow(start);
                                            const end = new Date(start);
                                            end.setMinutes(start.getMinutes() + 30);
                                            onSlotCreate(start, end);
                                        }, children: "+ New block" })] })] }), !editingAwake && (_jsxs("div", { className: "day-strip", children: [view === "day" && summary && (_jsxs(_Fragment, { children: [_jsxs("span", { className: `day-strip-stat ${summary.meets_lockout_target ? "ok" : "warn"}`, title: `Deep Focus — uninterrupted time without multi-tasking. Target ${summary.targets.lockout_blocks} × 30 min. ${summary.lockout_hours}h scheduled today.`, children: [_jsx("span", { className: "day-strip-dot dot-focus", "aria-hidden": "true" }), "Deep Focus", _jsxs("strong", { children: [summary.lockout_blocks, "/", summary.targets.lockout_blocks] })] }), _jsxs("span", { className: `day-strip-stat ${summary.meets_flex_target ? "ok" : "warn"}`, title: "Flex \u2014 catch-up, email replies, messaging co-workers, light admin work.", children: [_jsx("span", { className: "day-strip-dot dot-flex", "aria-hidden": "true" }), "Flex", _jsxs("strong", { children: [summary.flex_hours, "h"] })] }), _jsxs("span", { className: "day-strip-stat", title: "Personal \u2014 time spent on personal projects.", children: [_jsx("span", { className: "day-strip-dot dot-personal", "aria-hidden": "true" }), "Personal", _jsxs("strong", { children: [summary.personal_hours, "h"] })] })] })), _jsx("span", { className: "day-strip-spacer" }), awake && (_jsxs("button", { type: "button", className: "day-strip-awake", onClick: openAwakeEditor, title: "Click to edit your awake window \u2014 saves as your default", children: [_jsx("span", { "aria-hidden": "true", children: "\uD83D\uDD50" }), " ", fmtAwake(awakeStart), "\u2013", fmtAwake(awakeEnd), awake.is_overridden && (_jsx("span", { className: "pill ok", style: { marginLeft: 6 }, children: "override" }))] }))] })), editingAwake && (_jsxs("div", { className: "awake-banner awake-banner-edit", children: [_jsx("span", { className: "muted small", children: "Awake window" }), _jsx(TimeSelect, { value: awakeStartEdit, onChange: setAwakeStartEdit }), _jsx("span", { className: "muted", children: "to" }), _jsx(TimeSelect, { value: awakeEndEdit, onChange: setAwakeEndEdit }), _jsx("button", { className: "btn-primary", onClick: saveAwake, disabled: awakeSaving, children: awakeSaving ? "Saving…" : "Save" }), _jsx("button", { className: "btn-link", onClick: () => setEditingAwake(false), disabled: awakeSaving, children: "Cancel" })] })), (() => {
                        const active = blocks.find((b) => {
                            const s = new Date(b.start_at).getTime();
                            const e = new Date(b.end_at).getTime();
                            return nowMs >= s && nowMs < e;
                        });
                        if (!active)
                            return null;
                        const endMs = new Date(active.end_at).getTime();
                        const minsLeft = Math.max(0, Math.round((endMs - nowMs) / 60000));
                        const endLabel = new Date(active.end_at).toLocaleTimeString([], {
                            hour: "numeric",
                            minute: "2-digit",
                        });
                        return (_jsxs("div", { className: "active-block-wrap", children: [_jsxs("div", { className: "active-block-widget", children: [_jsx("span", { className: `active-block-dot dot-${active.block_type}`, "aria-hidden": "true" }), _jsxs("div", { className: "active-block-body", children: [_jsx("span", { className: "active-block-label", children: "In progress now" }), _jsx("span", { className: "active-block-title", children: active.title })] }), _jsxs("span", { className: "active-block-meta", children: [minsLeft, " min left \u00B7 ends ", endLabel] }), _jsx(Link, { to: "/focus", className: "active-block-focus", children: "Focus" })] }), _jsx(BlockResources, { projectId: active.project_id, variant: "panel" })] }));
                    })(), view === "day" ? (_jsxs(_Fragment, { children: [_jsx(MyDaySummary, { onDate: date, blocks: blocks, refreshSignal: refreshTick, 
                                // Reload the calendar after the user resolves an
                                // awaiting-review row so the tile's "Complete?" button
                                // updates immediately.
                                onBlockUpdated: reload, 
                                // Open the full ProgressPromptModal on "Not yet" so the
                                // user gets the percent + reschedule / handoff / schedule-
                                // later flow instead of just a silent kill-list re-add.
                                onOpenProgressPrompt: (row) => {
                                    // Find the full TimeBlock from the loaded calendar
                                    // blocks. The modal needs the whole record (block_type,
                                    // task_id, checklist_item_id, task_progress_percent).
                                    // If the calendar hasn't reloaded recently the row
                                    // might not be in `blocks`; fall through to synthesize
                                    // one from the awaiting-review payload.
                                    const live = blocks.find((b) => b.id === row.id);
                                    const synthetic = live ?? {
                                        id: row.id,
                                        user_id: "",
                                        title: row.title,
                                        description: null,
                                        start_at: row.end_at, // best-effort
                                        end_at: row.end_at,
                                        block_type: row.block_type,
                                        is_personal: row.is_personal,
                                        is_completed: false,
                                        checklist_item_id: row.checklist_item_id,
                                        recurring_pattern_id: null,
                                        task_id: row.task_id,
                                        task_progress_percent: 0,
                                        project_id: row.project_id,
                                        project_name: row.project_name ?? null,
                                        task_title: row.task_title ?? null,
                                        checklist_item_title: null,
                                        checklist_position: null,
                                        checklist_total: null,
                                        redacted: false,
                                    };
                                    setProgressBlock(synthetic);
                                } }), _jsx(CalendarGrid, { date: date, awakeStart: awakeStart, awakeEnd: awakeEnd, blocks: blocks, onCreate: onSlotCreate, onSelect: onBlockSelect, onMove: onBlockMove, onProgressPrompt: openProgressPrompt, nowTick: nowTick })] })) : (_jsxs(_Fragment, { children: [_jsx(MyWeekSummary, { monday: monday }), _jsx(WeekGrid, { weekStart: monday, awakeStart: awakeStart, awakeEnd: awakeEnd, blocks: blocks, onCreate: onSlotCreate, onSelect: onBlockSelect, nowTick: nowTick })] }))] }), _jsx(SchedulableItemsPanel, { open: panelOpen, onClose: () => setPanelOpen(false), onScheduleAsBlock: scheduleAsBlock, onScheduleTaskAsBlock: scheduleTaskAsBlock, onScheduleProjectAsBlock: scheduleProjectAsBlock, onScheduleAsRecurring: scheduleAsRecurring, onScheduleKillItem: scheduleKillItem, onSchedulePersonalTodo: schedulePersonalTodo, onScheduleGoal: scheduleGoalAsBlock, onScheduleGoalDays: scheduleGoalDays, refreshTick: refreshTick, 
                // Bump the tick when the in-scheduler CalendarSearch schedules a
                // new block so the day/week grid re-pulls and the new block shows
                // up without the user needing a manual refresh.
                onScheduled: () => setRefreshTick((t) => t + 1) }), _jsx(BlockEditor, { open: editorOpen, initialStart: editorStart, initialEnd: editorEnd, existing: editing, prefill: prefill, onClose: () => {
                    setEditorOpen(false);
                }, onSaved: async () => {
                    await reload();
                }, onPromptProgress: (block) => {
                    // BlockEditor closed itself before calling this — open the
                    // progress modal next paint so React commits both state
                    // changes cleanly.
                    setProgressBlock(block);
                } }), _jsx(ProgressPromptModal
            // Reset internal state per block via `key` — same trick we use on the
            // recurring editor when the prefill changes.
            , { open: progressBlock !== null, blockTitle: progressBlock?.title ?? "", blockId: progressBlock?.id ?? null, blockType: progressBlock?.block_type ?? null, taskId: progressBlock?.task_id ?? null, checklistItemId: progressBlock?.checklist_item_id ?? null, initialPercent: progressBlock?.task_progress_percent ?? 0, blockStartAt: progressBlock?.start_at ?? null, blockEndAt: progressBlock?.end_at ?? null, goalId: progressBlock?.goal_id ?? null, goalUnit: progressBlock?.goal_unit ?? null, goalSessionTarget: progressBlock?.goal_session_target ?? null, onClose: () => setProgressBlock(null), onSaved: reload, onScheduleFollowUp: () => {
                    if (progressBlock)
                        scheduleFollowUp(progressBlock);
                } }, progressBlock?.id ?? "blank"), _jsx(RecurringPatternEditor
            // Key on the source task — when the user clicks a different recurring
            // event in the side panel, this forces a full remount so the form
            // re-initializes from the new prefill (without it the useState
            // defaults from the first task stay stuck).
            , { open: recurringOpen, initialTitle: recurringPrefill?.title ?? "", initialFrequency: recurringPrefill?.recurrence_frequency ?? null, initialDaysOfWeek: recurringPrefill?.recurrence_days_of_week ?? null, initialDayOfMonth: recurringPrefill?.recurrence_day_of_month ?? null, initialTimeOfDay: recurringPrefill?.recurrence_time_of_day ?? null, initialDurationMinutes: recurringPrefill?.recurrence_duration_minutes ?? null, initialTimezone: recurringPrefill?.recurrence_timezone ?? null, sourceTaskId: recurringPrefill?.task_id ?? null, onClose: () => setRecurringOpen(false), onSaved: reload }, recurringPrefill?.task_id ?? "blank")] }));
}
